import { useDraft } from './useDraftContext';
import { bandLayout } from './layout';
import { UNASSIGNED_PHASE_ID } from './types';

/**
 * The phases a strip renders, ordered ones apart from the holding pen.
 *
 * Two hooks rather than one because `usePhaseAccents` runs a full geometry
 * pass: the strip's frame and its tabs are separate components, and the frame
 * has no use for accents. Bundling them would have run `bandLayout` twice on
 * every builder render to answer a question only one caller asked.
 */
export function usePhaseStripPhases() {
  const { draft, edit } = useDraft();

  return {
    draft,
    edit,
    ordered: draft.phases.filter((phase) => phase.id !== UNASSIGNED_PHASE_ID),
    unassigned: draft.phases.find((phase) => phase.id === UNASSIGNED_PHASE_ID),
  };
}

/**
 * Each phase's accent index, read from the same band layout the canvas draws
 * rather than recomputed here.
 *
 * Both used `index % PHASE_ACCENT_COUNT` and so happened to agree — but a tab
 * whose colour is derived separately from the band it names is a pair that
 * drifts the first time either rule changes. Reading it makes them the same
 * fact.
 */
export function usePhaseAccents(): Map<string | null, number> {
  const { draft } = useDraft();

  return new Map(
    bandLayout(draft).bands.map((band) => [band.id, band.accentIndex]),
  );
}
